import type { Field, Section } from '@eformulieren/model'
import type * as Y from 'yjs'
import { type Origin, isMergedText, originOf, setText, setValue, textOf, valuesMap } from './sectionDocument.js'

/**
 * Voorvullen via de querystring (R-37): welke velden het mag, wanneer, en hoe het in het document komt.
 *
 * Eén plek voor drie afnemers: de schil die zonder server werkt (het voorbeeld van de configurator,
 * de tests), de pagina die de waarden naar de pod stuurt, en de pod die ze in zijn document zet.
 * Liepen die regels uiteen, dan vulde de pod iets anders dan de invuller verwacht.
 *
 * **Waarom de pod het doet en niet de browser.** Sinds een tekstveld een `Y.Text` is (R-72) voegt
 * Yjs twee gelijktijdige invoegingen samen in plaats van er één te laten winnen. Twee mensen die
 * op hetzelfde moment dezelfde link openen, zien allebei een leeg veld, en als ze allebei schrijven
 * staat de tekst er twee keer. Met de waardenmap was dat onschuldig (twee keer dezelfde waarde is
 * één waarde), nu niet meer. Dit is precies het geval dat CLAUDE.md noemt bij de valkuilen: twee
 * gelijktijdige openers die allebei een leeg formulier vullen, en het antwoord is de server met
 * gezag. De pod heeft één document per sectie en kijkt en schrijft in één synchrone stap
 * (`apps/runtime/src/prefill.ts`), dus het tweede verzoek vindt het veld al gevuld en doet niets.
 */

/** Het soort bericht dat de pagina over de samenwerkverbinding stuurt. */
export const PREFILL_MESSAGE = 'voorvulling'

/**
 * Of dit veld deze waarde uit de querystring mag krijgen.
 *
 * Alleen `text`, `textarea`, `radio` en `dropdown`: hun waarde is één tekst, rechtstreeks
 * vergelijkbaar met een querystring-parameter. Een keuzeveld alleen met een waarde die in zijn
 * opties staat, want elke control die het tekent gaat ervan uit dat een opgeslagen waarde een
 * bekende optie is.
 */
export function mayPrefill(field: Field, value: string): boolean {
  if (field.control === 'radio' || field.control === 'dropdown') {
    return field.opties.some((option) => option.waarde === value)
  }
  return isMergedText(field.control)
}

/**
 * Of het veld nog leeg is in de zin van de voorvulling: er staat niets en er is nooit iets geschreven.
 *
 * Een veld dat iemand leegmaakte heeft een herkomst en blijft leeg; de voorvulling overschrijft
 * nooit een antwoord, ook geen leeg antwoord.
 */
export function emptyForPrefill(doc: Y.Doc, field: Field): boolean {
  if (isMergedText(field.control)) return textOf(doc, field.id) === '' && originOf(doc, field.id) === undefined
  return valuesMap(doc).get(field.id) === undefined
}

/** Zet de voorgevulde waarde, met de herkomst van wie het formulier opende. */
export function writePrefill(doc: Y.Doc, field: Field, value: string, origin: Origin): void {
  if (isMergedText(field.control)) setText(doc, field.id, value, origin)
  else setValue(doc, field.id, value, origin)
}

/** De velden van deze sectie die deze voorvulling mag vullen, met hun waarde, zonder naar het document te kijken. */
export function prefillCandidates(
  section: Pick<Section, 'velden'>,
  values: Readonly<Record<string, unknown>>,
): readonly { readonly field: Field; readonly value: string }[] {
  const out: { field: Field; value: string }[] = []
  for (const field of section.velden) {
    const value = values[field.id]
    if (typeof value !== 'string') continue
    if (mayPrefill(field, value)) out.push({ field, value })
  }
  return out
}

/** Het bericht met de voorvulling van één sectie, zoals de pagina het naar de pod stuurt. */
export function prefillMessage(values: Readonly<Record<string, string>>): string {
  return JSON.stringify({ soort: PREFILL_MESSAGE, waarden: values })
}

/**
 * De waarden uit een bericht, of niets als het geen voorvulling is of niet de vorm heeft.
 *
 * Wat de client stuurt is invoer: geen JSON, een ander soort, of een waarde die geen tekst is,
 * levert niets op, en de pod doet dan niets.
 */
export function readPrefillMessage(payload: string): Readonly<Record<string, string>> | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(payload)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined
  const message = parsed as { soort?: unknown; waarden?: unknown }
  if (message.soort !== PREFILL_MESSAGE) return undefined
  if (typeof message.waarden !== 'object' || message.waarden === null || Array.isArray(message.waarden)) return undefined
  const values: Record<string, string> = {}
  for (const [fieldId, value] of Object.entries(message.waarden as Record<string, unknown>)) {
    if (typeof value !== 'string') return undefined
    values[fieldId] = value
  }
  return values
}
